import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { API_BASE_URL, reportTakeAction, fetchIndividualReport, reactivateUsers } from "../../../api/endpoint";
import { Modal, Button, Form } from "react-bootstrap";
import Loader from "../../../components/loader";
import toast from "react-hot-toast";
import axios from "axios";

const UserReportDetailsPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const { auth } = useSelector((state) => state.login);

  const [report, setReport] = useState(null);
  const [data, setData] = useState({});
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!state?.reportId) return;
    loadDetails();
  }, [state?.reportId]);

  const loadDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${fetchIndividualReport}${state?.reportId}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      setReport(res?.data);
      const userData = res?.data?.additionalData || {};
      setData(userData);
      if (res?.data?.attachmentUrl) {
        setAttachmentUrl(`${API_BASE_URL}public/images/${res?.data?.attachmentUrl}`);
      } else {
        setAttachmentUrl(null);
      }
    } catch (err) {
      console.error("Notification details error:", err);
    } finally {
      setLoading(false);
    }
  };

  const userId = data?.id;
  const isUserSuspended = data?.suspended;

  const fields = [
    { label: "Name", value: data.name },
    { label: "Email", value: data.email },
    { label: "Phone", value: data.mobileNumber },
    { label: "Gender", value: data.gender },
    { label: "Age", value: data.age },
    { label: "City", value: data.city },
    { label: "State", value: data.state },
    { label: "Country", value: data.country },
    { label: "Zipcode", value: data.zipcode },
    { label: "Bio", value: data.note },
    { label: "Reliability", value: data.reliabilityNum ? `${data.reliabilityNum}%` : "—" },
    { label: "Games Joined", value: data.gamesSignedUpForNum },
    { label: "Games Attended", value: data.gamesAttendedNum },
  ];

  const openActionModal = (type) => {
    setActionType(type);
    setMessage("");
    setShowActionModal(true);
  };

  const handleActionConfirm = async () => {
    if (!actionType) return;

    try {
      setSubmitting(true);

      const payload = {
        reportId: report.reportId,
        reportUserId: userId,
        suspend: actionType === "SUSPEND",
        status: "ACTION_TAKEN",
        message: message,
      };

      await axios.post(reportTakeAction, payload, {
        headers: {
          Authorization: `Bearer ${auth}`,
        },
      });

      toast.success(actionType === "SUSPEND" ? "User has been suspended." : "Warning has been issued.");
      setShowActionModal(false);
      loadDetails();
    } catch (err) {
      console.error(err);
      toast.error("Failed to process action.");
    } finally {
      setSubmitting(false);
    }
  };

  const reactivateUser = async () => {
    try {
      setSubmitting(true);
      await axios.post(reactivateUsers, { authIds: [userId] }, { headers: { Authorization: `Bearer ${auth}` } });
      setShowReactivateModal(false)
      toast.success("User reactivated successfully");
      loadDetails();
    } catch (error) {
      console.error("Error updating user status", error);
      toast.error(error?.response?.data?.user_description || "Error reactivating user status");
    } finally {
      setSubmitting(false);
    }
  };

  if (!state?.reportId) {
    return <div className="p-4">No report selected.</div>;
  }

  return (
    <div className="container py-4">
      <Button variant="secondary" onClick={() => navigate(-1)}>
        ← Back
      </Button>

      <h3 className="mt-3 mb-4">User Report Details</h3>

      {/* -------- User Info Table -------- */}
      <div className="card p-3 mb-4">
        <table className="table table-borderless mb-0">
          <tbody>
            {fields.map((item, idx) => (
              <tr key={idx}>
                <td className="fw-semibold" style={{ width: "25%" }}>
                  {item.label}:
                </td>
                <td>{item.value ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* -------- Avatar -------- */}
      {data.avatarUrl && (
        <div className="mb-4">
          <h5>Profile Photo</h5>
          <img
            src={data.avatarUrl}
            alt="avatar"
            style={{
              maxWidth: "150px",
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
        </div>
      )}

      {/* -------- User Images -------- */}
      {data.images?.image?.length > 0 && (
        <div className="mb-4">
          <h5>User Images</h5>
          <div className="d-flex flex-wrap gap-3">
            {data.images.image.map((img, idx) => (
              <img
                key={idx}
                src={`${API_BASE_URL}public/images/${img}`}
                alt="user upload"
                style={{
                  maxWidth: "150px",
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* -------- Report Attachment -------- */}
      {attachmentUrl && (
        <div className="mb-4">
          <h5>Report Attachment</h5>
          <img
            src={attachmentUrl}
            alt="attachment"
            style={{
              maxWidth: "100%",
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {/* -------- Action Buttons -------- */}
      <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
        {isUserSuspended ? (
          <>
            <span className="text-danger fw-semibold">User is suspended</span>
            <Button variant="success" onClick={() => setShowReactivateModal(true)}>
              Reactivate User
            </Button>
          </>
        ) : (
          <>
            <Button variant="warning" onClick={() => openActionModal("WARN")}>
              Send Warning
            </Button>
            <Button variant="danger" onClick={() => openActionModal("SUSPEND")}>
              Suspend User
            </Button>
          </>
        )}
      </div>

      {/* -------- Action Confirmation Modal -------- */}
      <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{actionType === "WARN" ? "Send Warning" : "Suspend User"}</h5>
          <button className="btn-close" onClick={() => setShowActionModal(false)}></button>
        </div>

        <div className="modal-body">
          <p>
            Are you sure you want to <strong>{actionType === "WARN" ? "warn" : "suspend"}</strong> this user?
          </p>
          <Form.Group>
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a message..."
            />
          </Form.Group>
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>

          <Button
            variant={actionType === "WARN" ? "warning" : "danger"}
            onClick={handleActionConfirm}
            disabled={submitting}
          >
            Confirm
          </Button>
        </div>
      </Modal>

      {/* -------- Reactivate User Confirmation Modal -------- */}
      <Modal show={showReactivateModal} onHide={() => setShowReactivateModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">Reactivate User</h5>
          <button className="btn-close" onClick={() => setShowReactivateModal(false)}></button>
        </div>

        <div className="modal-body">
          <p>
            Are you sure you want to <strong>reactivate</strong> this user?
          </p>
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>

          <Button
            // variant={actionType === "WARN" ? "warning" : "danger"}
            onClick={reactivateUser}
            disabled={submitting}
          >
            Confirm
          </Button>
        </div>
      </Modal>
      {loading && <Loader />}
    </div>
  );
};

export default UserReportDetailsPage;
